import Dexie from 'dexie'
import { IDBKeyRange as inMemoryIDBKeyRange, indexedDB as inMemoryDb } from 'fake-indexeddb'

export const iDBSupported = 'indexedDB' in window
const SHOW_DEBUG_WINDOW = import.meta.env.REACT_APP_SHOW_DEBUG_WINDOW === 'true'
export interface Log {
  id?: number
  traceId: string
  timestamp: number
  level: 'info' | 'warn' | 'error' | 'debug'
  message: string
  details?: unknown
}

export type LoggerType = 'db' | 'memory' | 'fallback'

export interface LoggerConfig {
  traceId: string
  loggers: LoggerType[]
  logToConsole?: boolean
  logLevelsToConsole?: Log['level'][]
  showDebugWindow?: boolean
  fallbackLoggerEnabled?: boolean
}

export interface Logger {
  init(config: LoggerConfig): void

  error(message: string, details?: unknown): Promise<void>

  debug(message: string, details?: unknown): Promise<void>

  warn(message: string, details?: unknown): Promise<void>

  info(message: string, details?: unknown): Promise<void>

  log(level: Log['level'], message: string, details?: unknown): Promise<void>

  clear(): Promise<void>

  deleteByTraceId(traceId: string): Promise<void>

  getLogsByTraceId(traceId: string, from?: LoggerType): Promise<Log[]>

  destroy(): Promise<void>
  getTraceId(): string | null
  subscribe(callback: (log: Log) => void): void
  unsubscribe(callback: (log: Log) => void): void
}

export interface LogTransport {
  log(logEntry: Log): Promise<void>

  getLogsByTraceId(traceId: string): Promise<Log[]>

  deleteByTraceId(traceId: string): Promise<void>

  clear(): Promise<void>

  destroy(): Promise<void>
}

export class LoggerDB extends Dexie {
  logs!: Dexie.Table<Log, number>

  constructor() {
    super('LoggerDB', {
      indexedDB: iDBSupported ? window.indexedDB : inMemoryDb,
      IDBKeyRange: iDBSupported ? window.IDBKeyRange : inMemoryIDBKeyRange,
    })

    this.version(1).stores({
      logs: '++id, traceId, timestamp, level',
    })
  }
}

export const loggerDb = new LoggerDB()

class DBLogger implements LogTransport {
  async log(logEntry: Log) {
    if (iDBSupported) {
      try {
        await loggerDb.logs.add(logEntry)
      } catch (error) {
        console.error('Failed to write log to IndexedDB:', error)
        throw error
      }
    } else {
      const error = new Error('IndexedDB is not supported.')
      console.error(error)
      throw error
    }
  }

  async getLogsByTraceId(traceId: string): Promise<Log[]> {
    if (iDBSupported) {
      try {
        return await loggerDb.logs.where('traceId').equals(traceId).sortBy('timestamp')
      } catch (error) {
        console.error(`Failed to fetch logs with traceId ${traceId} from IndexedDB:`, error)
        throw error
      }
    }
    return []
  }

  async deleteByTraceId(traceId: string): Promise<void> {
    if (iDBSupported) {
      try {
        await loggerDb.logs.where('traceId').equals(traceId).delete()
      } catch (error) {
        console.error(`Failed to delete logs with traceId ${traceId} from IndexedDB:`, error)
        throw error
      }
    }
  }

  async clear(): Promise<void> {
    if (iDBSupported) {
      try {
        await loggerDb.logs.clear()
      } catch (error) {
        console.error('Failed to clear logs from IndexedDB:', error)
        throw error
      }
    }
  }

  async destroy(): Promise<void> {
    if (iDBSupported) {
      try {
        loggerDb.close()
        return Promise.resolve()
      } catch (error) {
        console.error('Failed to delete database:', error)
        return Promise.reject(error)
      }
    }
  }
}

class InMemoryLogger implements LogTransport {
  private logs: Log[] = []

  async log(logEntry: Log) {
    this.logs.push(logEntry)
  }

  async getLogsByTraceId(traceId: string): Promise<Log[]> {
    return Promise.resolve(this.logs.filter((log) => log.traceId === traceId))
  }

  async deleteByTraceId(traceId: string): Promise<void> {
    this.logs = this.logs.filter((log) => log.traceId !== traceId)
    return Promise.resolve()
  }

  async clear(): Promise<void> {
    this.logs = []
    return Promise.resolve()
  }

  async destroy(): Promise<void> {
    this.logs = []
    return Promise.resolve()
  }
}

class FallbackLogger implements LogTransport {
  private textarea: HTMLTextAreaElement | null = null
  private container: HTMLDivElement | null = null
  private readonly containerId = 'logger-fallback-container'
  private readonly textareaId = 'logger-fallback-textarea'
  private showUI: boolean

  constructor(options: { showDebugWindow?: boolean } = {}) {
    this.showUI = options.showDebugWindow || false

    if (this.showUI) {
      this.initUI()
    }
  }

  private initUI() {
    if (document.getElementById(this.containerId)) {
      this.container = document.getElementById(this.containerId) as HTMLDivElement
      this.textarea = document.getElementById(this.textareaId) as HTMLTextAreaElement
      return
    }

    this.container = document.createElement('div')
    this.container.id = this.containerId
    Object.assign(this.container.style, {
      position: 'fixed',
      bottom: '0',
      left: '50%',
      transform: 'translate(-50%, calc(100% - 30px))',
      width: '600px',
      maxWidth: '90vw',
      zIndex: '9999',
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '0.25rem 0.25rem 0 0',
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease-in-out',
    })

    const header = document.createElement('div')
    Object.assign(header.style, {
      padding: '5px 10px',
      backgroundColor: '#e9ecef',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #dee2e6',
      height: '30px',
      boxSizing: 'border-box',
    })

    const title = document.createElement('span')
    title.textContent = 'Log Viewer'
    header.appendChild(title)

    const copyLink = document.createElement('a')
    copyLink.textContent = 'Copy to Clipboard'
    Object.assign(copyLink.style, {
      cursor: 'pointer',
      color: '#007bff',
      textDecoration: 'underline',
      fontSize: '12px',
    })
    copyLink.onclick = (e) => {
      e.stopPropagation()
      if (this.textarea) {
        navigator.clipboard.writeText(this.textarea.value).then(
          () => {
            copyLink.textContent = 'Copied!'
            setTimeout(() => {
              copyLink.textContent = 'Copy to Clipboard'
            }, 2000)
          },
          (err) => {
            console.error('Failed to copy logs:', err)
          },
        )
      }
    }
    header.appendChild(copyLink)

    header.onclick = () => {
      if (this.container) {
        const isCollapsed = this.container.style.transform === 'translate(-50%, calc(100% - 30px))'
        this.container.style.transform = isCollapsed ? 'translate(-50%, 0px)' : 'translate(-50%, calc(100% - 30px))'
      }
    }

    this.textarea = document.createElement('textarea')
    this.textarea.id = this.textareaId
    this.textarea.readOnly = true
    Object.assign(this.textarea.style, {
      height: '400px',
      width: '100%',
      border: 'none',
      padding: '5px',
      boxSizing: 'border-box',
      fontFamily: 'monospace',
      fontSize: '12px',
      resize: 'vertical',
    })

    this.container.appendChild(header)
    this.container.appendChild(this.textarea)

    if (this.showUI) {
      document.body.appendChild(this.container)
    }
  }

  async log(logEntry: Log): Promise<void> {
    if (!this.textarea) {
      this.initUI()
    }

    if (this.textarea) {
      const logString = JSON.stringify(logEntry)
      this.textarea.value += logString + '\n'
      this.textarea.scrollTop = this.textarea.scrollHeight
    }
    return Promise.resolve()
  }

  async getLogsByTraceId(traceId: string): Promise<Log[]> {
    if (!this.textarea) {
      return Promise.resolve([])
    }

    try {
      const lines = this.textarea.value.split('\n').filter(Boolean)
      const logs: Log[] = lines.map((line) => JSON.parse(line))
      return logs.filter((log) => log.traceId === traceId)
    } catch (error) {
      console.error('Failed to parse logs from fallback textarea:', error)
      return Promise.resolve([])
    }
  }

  async deleteByTraceId(traceId: string): Promise<void> {
    if (this.textarea) {
      const lines = this.textarea.value.split('\n').filter(Boolean)
      const remainingLines = lines.filter((line) => {
        try {
          const log: Log = JSON.parse(line)
          return log.traceId !== traceId
        } catch {
          return true
        }
      })
      this.textarea.value = remainingLines.join('\n') + (remainingLines.length > 0 ? '\n' : '')
    }
    return Promise.resolve()
  }

  async clear(): Promise<void> {
    if (this.textarea) {
      this.textarea.value = ''
    }
    return Promise.resolve()
  }

  async destroy(): Promise<void> {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
    }
    this.container = null
    this.textarea = null
    return Promise.resolve()
  }
}

export class AppLogger implements Logger {
  private traceId: string | null = null
  private transports: LogTransport[] = []
  private logToConsole: boolean = false
  private logLevelsToConsole: Log['level'][] = []
  private subscribers: ((log: Log) => void)[] = []
  private fallbackLoggerEnabled: boolean = false

  init(config: LoggerConfig) {
    this.traceId = config.traceId
    this.transports = []
    this.logToConsole = config.logToConsole ?? false
    this.logLevelsToConsole = config.logLevelsToConsole ?? []
    this.fallbackLoggerEnabled = config.fallbackLoggerEnabled || false

    if (config.loggers.includes('db')) {
      this.transports.push(new DBLogger())
    }

    if (config.loggers.includes('memory')) {
      this.transports.push(new InMemoryLogger())
    }

    if (config.loggers.includes('fallback') || this.fallbackLoggerEnabled) {
      const shouldShowDebugWindow = this.shouldShowDebugWindow(config)
      this.transports.push(new FallbackLogger({ showDebugWindow: shouldShowDebugWindow }))
    }
  }

  shouldShowDebugWindow = (config: LoggerConfig) => {
    const urlParams = new URLSearchParams(window.location.search)
    const showFromUrl = urlParams.get('showLoggerDebug') === 'true'
    const showFromConfig = config.showDebugWindow === true
    return SHOW_DEBUG_WINDOW || showFromUrl || showFromConfig
  }

  async error(message: string, details?: unknown) {
    await this.log('error', message, details)
  }

  async debug(message: string, details?: unknown) {
    await this.log('debug', message, details)
  }

  async warn(message: string, details?: unknown) {
    await this.log('warn', message, details)
  }

  async info(message: string, details?: unknown) {
    await this.log('info', message, details)
  }

  private _logToConsole(logEntry: Log) {
    const { level, message, details } = logEntry
    const shouldLog = this.logLevelsToConsole.length === 0 || this.logLevelsToConsole.includes(level)
    if (shouldLog) {
      const logArgs: [string, unknown?] = [message]
      if (details !== undefined) {
        logArgs.push(details)
      }

      switch (level) {
        case 'info':
          console.info(...logArgs)
          break
        case 'debug':
          console.debug(...logArgs)
          break
        case 'warn':
          console.warn(...logArgs)
          break
        case 'error':
          console.error(...logArgs)
          break
        default:
          console.log(...logArgs)
      }
    }
  }

  async log(level: Log['level'], message: string, details?: unknown) {
    const logEntry: Log = {
      timestamp: Date.now(),
      level,
      message,
      details,
      traceId: this.traceId ?? 'trace-id',
    }
    if (this.logToConsole) {
      this._logToConsole(logEntry)
    }

    const logPromises = this.transports.map((transport) => transport.log(logEntry))

    this.subscribers.forEach((callback) => {
      try {
        callback(logEntry)
      } catch (e) {
        console.error('Error in logger subscriber:', e)
      }
    })

    const results = await Promise.allSettled(logPromises)

    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.error('Logger failed:', result.reason)
      }
    })
  }

  async clear() {
    const promises = this.transports.map((t) => t.clear())
    await Promise.allSettled(promises)
  }

  async deleteByTraceId(traceId: string) {
    const promises = this.transports.map((t) => t.deleteByTraceId(traceId))
    await Promise.allSettled(promises)
  }

  async getLogsByTraceId(traceId: string, from?: LoggerType): Promise<Log[]> {
    let transportsToQuery = this.transports

    if (from) {
      if (from === 'db') {
        transportsToQuery = this.transports.filter((t) => t instanceof DBLogger)
      } else if (from === 'memory') {
        transportsToQuery = this.transports.filter((t) => t instanceof InMemoryLogger)
      } else if (from === 'fallback') {
        transportsToQuery = this.transports.filter((t) => t instanceof FallbackLogger)
      }
    }

    const logPromises = transportsToQuery.map((t) => t.getLogsByTraceId(traceId))
    const results = await Promise.allSettled(logPromises)
    const allLogs: Log[] = []
    let hasFulfilled = false

    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        allLogs.push(...result.value)
        if (result.value.length > 0) {
          hasFulfilled = true
        }
      }
    })

    if (this.fallbackLoggerEnabled && !hasFulfilled && results.some((r) => r.status === 'rejected')) {
      console.warn('Primary log transports failed. Attempting to read from fallback logger.')
      const fallbackLogger = this.transports.find((t) => t instanceof FallbackLogger)
      if (fallbackLogger) {
        try {
          const fallbackLogs = await fallbackLogger.getLogsByTraceId(traceId)
          if (fallbackLogs.length > 0) {
            allLogs.push(...fallbackLogs)
          }
        } catch (e) {
          console.error('Failed to read from fallback logger:', e)
        }
      }
    }

    const uniqueLogs = Array.from(new Map(allLogs.map((log) => [JSON.stringify(log), log])).values())

    return uniqueLogs.sort((a, b) => a.timestamp - b.timestamp)
  }

  async deleteDatabase() {
    const promises = this.transports.map((t) => t.destroy())
    await Promise.allSettled(promises)
  }

  async destroy() {
    await this.deleteDatabase()
    this.traceId = null
    this.transports = []
    this.logToConsole = false
    this.logLevelsToConsole = []
    this.subscribers = []
  }

  getTraceId(): string | null {
    return this.traceId
  }

  subscribe(callback: (log: Log) => void) {
    this.subscribers.push(callback)
  }

  unsubscribe(callback: (log: Log) => void) {
    this.subscribers = this.subscribers.filter((cb) => cb !== callback)
  }
}
